import {
	IsArray,
	IsBoolean,
	IsEmpty,
	IsInt,
	IsNotEmpty,
	IsNumber,
	IsOptional,
	IsString,
	IsUUID,
	Min,
	ValidateNested,
	IsEnum, ValidateIf, Max,
} from 'class-validator';
import { Type } from 'class-transformer';
import { EventDto } from './event.dto';
import { EventTicketDto } from './event-ticket.dto';
import { EventToUserDto } from "./event-to-user.dto";
import { EventPriceTypesEnum, DiscountTypeEnum } from '../../enums';
import { IsHigherThan } from '../../decorators';

export class EventPriceDto {
	public id: string;
	public basePriceId: string;
	public priceAmount: number;
	public currency: string;
	public tierName: string;
	public tierDescription?: string;
	public tierTypeId: EventPriceTypesEnum;
	public maxTicketsPerOrder?: number;
	public minTicketsPerOrder?: number;
	public isBaseTier: boolean;
	public order: number;
	public createdAt: Date;
	public updatedAt: Date;

	public ticketsCount?: number;
	public maxTickets?: number;

	public events?: EventDto[];
	public eventTickets?: EventTicketDto[];
	public eventToUsers?: EventToUserDto[];
	public eventPriceToMemberships?: EventPriceToMembershipDto[]
}

export class EventPriceToMembershipDto {
	public id: string;
	public eventPriceId: string;
	public membershipId: string;
	public amount: number;
	public type: DiscountTypeEnum;
}

export class UpsertEventPriceToMembershipsDto {
	@IsUUID()
	@IsNotEmpty()
	public membershipId: string;

	@IsInt()
	@IsNotEmpty()
	public amount: number;

	@IsEnum(DiscountTypeEnum)
	@IsNotEmpty()
	public type: DiscountTypeEnum;

	/**
	 * Can join for free: { amount: 100, type: 'percentage' }
	 * Half the price of the ticket: { amount: 50, type: 'percentage' }
	 * Full price: { amount: 0, type: 'percentage' } OR NO RECORD
	 * Custom: { amount: 1000, type: 'fixed' } OR { amount: 25, type: 'percentage' }
	 * */
}

export class CreateEventPriceDto {
	@IsEmpty()
	public basePriceId?: string;

	@IsNumber()
	@IsInt()
	@Min(0)
	@IsNotEmpty()
	public priceAmount: number;

	@IsString()
	@IsNotEmpty()
	public tierName: string;

	@IsOptional()
	@IsEnum(EventPriceTypesEnum)
	public tierTypeId?: EventPriceTypesEnum;

	@ValidateIf(o => o.tierTypeId === EventPriceTypesEnum.Group)
	@IsNumber()
	@IsInt()
	@Min(2)
	@IsNotEmpty()
	public minTicketsPerOrder?: number;

	@ValidateIf(o => o.tierTypeId === EventPriceTypesEnum.Group)
	@IsNumber()
	@IsInt()
	@IsNotEmpty()
	@IsHigherThan('minTicketsPerOrder')
	public maxTicketsPerOrder?: number;

	@IsOptional()
	@IsString()
	public currency?: string;

	@IsOptional()
	@IsString()
	public tierDescription?: string;

	@IsOptional()
	@IsBoolean()
	public isBaseTier?: boolean;

	@IsOptional()
	@IsNumber()
	public maxTickets?: number | null;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => UpsertEventPriceToMembershipsDto)
	public upsertEventPricesToMemberships?: UpsertEventPriceToMembershipsDto[];

	@IsOptional()
	@IsInt()
	@Min(0)
	public order: number;
}

export class UpdateEventPriceDto implements Partial<CreateEventPriceDto> {
	@IsUUID()
	@IsNotEmpty()
	public id: string;

	@IsOptional()
	@IsInt()
	@Min(0)
	@IsNotEmpty()
	public priceAmount?: number;

	@IsOptional()
	@IsString()
	@IsNotEmpty()
	public tierName?: string;

	@IsOptional()
	@IsEnum(EventPriceTypesEnum)
	public tierTypeId?: EventPriceTypesEnum;

	@ValidateIf(o => o.tierTypeId === EventPriceTypesEnum.Group)
	@IsNumber()
	@IsInt()
	@Min(2)
	@IsNotEmpty()
	public minTicketsPerOrder?: number;

	@ValidateIf(o => o.tierTypeId === EventPriceTypesEnum.Group)
	@IsNumber()
	@IsInt()
	@IsNotEmpty()
	@IsHigherThan('minTicketsPerOrder')
	public maxTicketsPerOrder?: number;

	@IsOptional()
	@IsString()
	public currency?: string;

	@IsOptional()
	@IsString()
	public tierDescription?: string;

	@IsOptional()
	@IsBoolean()
	public isBaseTier?: boolean;

	@IsOptional()
	@IsNumber()
	public maxTickets?: number | null;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => UpsertEventPriceToMembershipsDto)
	public upsertEventPricesToMemberships?: UpsertEventPriceToMembershipsDto[];
}
